import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { ArrowLeft, CheckCircle, AlertTriangle, Link2, FileDown, FileCode, RotateCw, ShieldX, GitCompare, Flag, Workflow, UserCircle, MessageSquare, GitFork } from "lucide-react";

/**
 * Chain Reconciliation Results (Phase A).
 * — Summary cards (total chains, complete, broken, ignored, branches dropped)
 * — Per-hop cards (file, rule set, matched, partial, ignored)
 * — Waterfall table: one row per chain, wide columns (hop0_src_*, hop0_dst_*,
 *   hop0_rule, hop1_*, …), status badge, status filter tabs
 * — Actions: export CSV, open HTML report, rerun, delete
 */

function StatusBadge({ status }) {
  let bg = "#e5e7eb", color = "#374151";
  if (status === "complete") { bg = "#dcfce7"; color = "#166534"; }
  else if (status.startsWith("broken")) { bg = "#fee2e2"; color = "#991b1b"; }
  else if (status.startsWith("ignored")) { bg = "#fef3c7"; color = "#92400e"; }
  return (
    <span data-testid={`chain-status-${status}`}
      style={{ display: "inline-block", padding: "0.1rem 0.5rem", borderRadius: "0.35rem",
               fontSize: "0.7rem", fontWeight: 600, background: bg, color, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
}

function Card({ label, value, color, sub }) {
  return (
    <div className="recon-card" style={{ padding: "1rem 1.25rem" }}>
      <p style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 600, margin: 0 }}>{label}</p>
      <p style={{ fontSize: "1.6rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, color, margin: "0.2rem 0 0" }}>{value}</p>
      {sub && <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", margin: "0.2rem 0 0" }}>{sub}</p>}
    </div>
  );
}

const TABS = [
  { id: "all",      label: "All chains",    color: "var(--sage)" },
  { id: "complete", label: "Complete",      color: "#16a34a"     },
  { id: "broken",   label: "Broken",        color: "#dc2626"     },
  { id: "ignored",  label: "Ignored",       color: "#a16207"     },
  { id: "conflict", label: "Conflicts",     color: "#f97316"     },
  { id: "branches", label: "Branched",      color: "#6366f1"     },
];

// Mini Sankey: inline SVG, no external lib. Lays out nodes in vertical
// columns based on an estimated column index (order-of-first-link appearance),
// and draws curved links sized by value.
function SankeyChart({ data }) {
  if (!data || !data.nodes?.length) return null;
  const { nodes, links } = data;
  // Assign each node an x-column by how many links lead into it (simple topological-ish sort)
  const col = new Array(nodes.length).fill(0);
  links.forEach(l => { col[l.target] = Math.max(col[l.target], col[l.source] + 1); });
  const colsMax = Math.max(...col) + 1;
  const width = 860, height = 280;
  const nodeW = 10, colPad = (width - nodeW) / Math.max(1, colsMax - 1);
  // Group nodes by column, stack them vertically proportional to their throughput
  const nodesByCol = {};
  nodes.forEach((n, i) => {
    const throughput = links.reduce((a, l) => a + (l.source === i || l.target === i ? l.value : 0), 0);
    nodesByCol[col[i]] = nodesByCol[col[i]] || [];
    nodesByCol[col[i]].push({ i, name: n.name, tp: throughput });
  });
  const positioned = {};
  Object.entries(nodesByCol).forEach(([c, ns]) => {
    const total = ns.reduce((a, x) => a + Math.max(1, x.tp), 0);
    let y = 10;
    ns.forEach(n => {
      const h = Math.max(10, (height - 40) * (Math.max(1, n.tp) / total));
      positioned[n.i] = { x: Number(c) * colPad, y, h, name: n.name };
      y += h + 6;
    });
  });
  // Colors: green for Complete, red for Broken, yellow for Ignored, blue for hops
  const colorFor = (name) => {
    if (name === "Complete") return "#16a34a";
    if (name === "Broken")   return "#dc2626";
    if (name === "Ignored")  return "#a16207";
    return "#8B5CF6";
  };
  return (
    <svg data-testid="chain-sankey-svg" viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: height, background: "white", border: "1px solid #e5e7eb", borderRadius: 8 }}>
      {links.map((l, i) => {
        const s = positioned[l.source], t = positioned[l.target];
        if (!s || !t) return null;
        const sx = s.x + nodeW, sy = s.y + s.h / 2;
        const tx = t.x, ty = t.y + t.h / 2;
        const mx = (sx + tx) / 2;
        const path = `M${sx},${sy} C${mx},${sy} ${mx},${ty} ${tx},${ty}`;
        return <path key={i} d={path} stroke={colorFor(positioned[l.target].name)} strokeOpacity={0.3} strokeWidth={Math.max(1.5, Math.min(20, l.value))} fill="none" />;
      })}
      {Object.values(positioned).map((p, i) => (
        <g key={i}>
          <rect x={p.x} y={p.y} width={nodeW} height={p.h} fill={colorFor(p.name)} />
          <text x={p.x + nodeW + 4} y={p.y + p.h / 2 + 4} fontSize={10} fill="#374151">{p.name}</text>
        </g>
      ))}
    </svg>
  );
}

export default function ChainResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [tab, setTab] = useState("all");
  const [waterfall, setWaterfall] = useState({ total: 0, results: [] });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sankey, setSankey] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(null); // conflict entry
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]); // hop_keys
  const [bulkPolicy, setBulkPolicy] = useState("highest_rank");
  const [bulkSpecificRule, setBulkSpecificRule] = useState("");
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);   // { preview: [...], would_resolve, would_remain }
  const [bulkPreviewBusy, setBulkPreviewBusy] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffAgainst, setDiffAgainst] = useState("");
  const [diffResult, setDiffResult] = useState(null);
  const [otherRuns, setOtherRuns] = useState([]);
  const [selectedChain, setSelectedChain] = useState(null);   // full chain doc
  const [assignEmail, setAssignEmail] = useState("");
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    api.get(`/chain-runs/${id}`).then(r => setDoc(r.data)).catch(() => navigate(-1));
    api.get(`/chain-runs/${id}/sankey`).then(r => setSankey(r.data)).catch(() => {});
    api.get("/chain-runs").then(r => setOtherRuns((r.data || []).filter(x => x.id !== id))).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const params = {};
    if (tab !== "all") params.status = tab;
    if (search) params.q = search;
    params.skip = 0; params.limit = 500;
    api.get(`/chain-runs/${id}/waterfall`, { params })
      .then(r => setWaterfall(r.data))
      .finally(() => setLoading(false));
  }, [id, tab, search]);

  const cols = useMemo(() => {
    if (!waterfall.results.length) return [];
    const base = ["chain_id", "status", "hops_completed", "branches_dropped"];
    const seen = new Set(base);
    const extra = [];
    for (const r of waterfall.results) {
      for (const k of Object.keys(r)) {
        if (!seen.has(k)) { extra.push(k); seen.add(k); }
      }
    }
    // Sort hop-prefixed extras by (hop, kind, name): src < rule < dst
    extra.sort((a, b) => {
      const parse = (c) => {
        const m = /^hop(\d+)_(.+)$/.exec(c);
        if (!m) return [9e9, 0, c];
        const hop = Number(m[1]);
        const rest = m[2];
        if (rest === "rule") return [hop, 1, ""];
        if (rest.startsWith("src_")) return [hop, 0, rest.slice(4)];
        if (rest.startsWith("dst_")) return [hop, 2, rest.slice(4)];
        return [hop, 3, rest];
      };
      const [ha, ka, na] = parse(a); const [hb, kb, nb] = parse(b);
      if (ha !== hb) return ha - hb;
      if (ka !== kb) return ka - kb;
      return na.localeCompare(nb);
    });
    return [...base, ...extra];
  }, [waterfall.results]);

  if (!doc) return <Layout><div style={{ padding: "2rem" }}>Loading…</div></Layout>;
  const s = doc.summary || {};

  const onExportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/chain-runs/${id}/export.csv${tab !== "all" ? `?status=${tab}` : ""}`;
    window.open(url, "_blank");
  };
  const onOpenReport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/chain-runs/${id}/report`, "_blank");
  };
  const onRerun = async () => {
    if (!window.confirm("Re-run this chain with the same config?")) return;
    try {
      const r = await api.post(`/chain-runs/${id}/rerun`);
      navigate(`/chain-results/${r.data.id}`);
    } catch (e) {
      alert(`Rerun failed: ${e.response?.data?.detail || e.message}`);
    }
  };
  const onDelete = async () => {
    if (!window.confirm(`Delete "${doc.name}"? This removes all hops and results.`)) return;
    try {
      await api.delete(`/chain-runs/${id}`);
      navigate(-1);
    } catch (e) {
      alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><ArrowLeft size={18} /></button>
            <div>
              <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Link2 size={20} style={{ color: "#8B5CF6" }} /> {doc.name}
              </h1>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>
                Chain reconciliation · {doc.legs.length} hop{doc.legs.length > 1 ? "s" : ""} · anchor <b>{doc.anchor_file_name}</b>
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button data-testid="chain-export-csv" onClick={onExportCsv} className="btn-secondary" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <FileDown size={12} /> CSV
            </button>
            <button data-testid="chain-export-pdf" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/chain-runs/${id}/pdf`, "_blank")}
              className="btn-secondary" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <FileDown size={12} /> PDF
            </button>
            <button data-testid="chain-open-report" onClick={onOpenReport} className="btn-secondary" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <FileCode size={12} /> Report
            </button>
            <button data-testid="chain-diff-btn" onClick={() => setDiffOpen(true)} className="btn-secondary" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <GitCompare size={12} /> Diff
            </button>
            <button data-testid="chain-rerun" onClick={onRerun} className="btn-secondary" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <RotateCw size={12} /> Re-run
            </button>
            <button data-testid="chain-delete" onClick={onDelete} className="btn-secondary" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem", color: "#dc2626" }}>
              <ShieldX size={12} /> Delete
            </button>
          </div>
        </div>

        {/* Anomaly banner */}
        {s.anomaly_flag && (
          <div data-testid="chain-anomaly-banner" style={{ padding: "0.75rem 1rem", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "0.5rem", color: "#92400e", fontSize: "0.85rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <AlertTriangle size={16} /> <b>Anomaly:</b> {s.anomaly_reason}
          </div>
        )}

        {/* Summary */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "0.75rem", marginBottom: "1.25rem" }}>
          <Card label="Total anchor"     value={s.total_anchor ?? 0}   color="#1E40AF" />
          <Card label="Chains complete"  value={s.chains_complete ?? 0}color="#16a34a" sub={s.total_anchor ? `${Math.round((s.chains_complete || 0) / s.total_anchor * 100)}% of anchor` : "—"} />
          <Card label="Chains broken"    value={s.chains_broken ?? 0}  color="#dc2626" />
          <Card label="Chains ignored"   value={s.chains_ignored ?? 0} color="#a16207" />
          <Card label="In conflict"      value={s.chains_conflict ?? 0} color="#f97316" sub={(s.chains_conflict ? "Click tab →" : "—")} />
          <Card label="Cost reconciled"  value={`$${(s.total_cost_complete ?? 0).toLocaleString()}`} color="#0891b2" sub={`$${(s.total_cost_broken ?? 0).toLocaleString()} at risk`} />
        </div>

        {/* Sankey */}
        {sankey && sankey.nodes && sankey.nodes.length > 0 && (
          <div className="recon-card" style={{ padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
              <Workflow size={12} /> Chain flow (Sankey)
            </div>
            <SankeyChart data={sankey} />
          </div>
        )}

        {/* Conflicts panel */}
        {(doc.conflicts || []).length > 0 && (
          <div className="recon-card" data-testid="chain-conflicts-panel" style={{ padding: "1rem 1.25rem", marginBottom: "1.25rem", background: "#fff7ed", borderLeft: "3px solid #f97316" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                ⚠ {doc.conflicts.length} conflict{doc.conflicts.length === 1 ? "" : "s"} — same target row claimed by multiple chains
              </div>
              {doc.conflicts.length >= 2 && (
                <button data-testid="bulk-resolve-open" onClick={() => { setBulkOpen(true); setBulkSelected([]); setBulkPreview(null); }}
                  className="btn-primary" style={{ marginLeft: "auto", fontSize: "0.75rem", padding: "0.25rem 0.65rem" }}>
                  Bulk resolve…
                </button>
              )}
            </div>
            {doc.conflicts.slice(0, 5).map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0", fontSize: "0.8rem" }}>
                <code style={{ background: "#ffedd5", padding: "0.1rem 0.4rem", borderRadius: "0.25rem", fontSize: "0.72rem" }}>{c.hop_key.split(":")[0]}</code>
                <span style={{ color: "var(--text-muted)" }}>{c.chain_ids.length} chains competing</span>
                <button data-testid={`resolve-conflict-${i}`} onClick={() => setResolveOpen(c)}
                  className="btn-secondary" style={{ fontSize: "0.72rem", marginLeft: "auto", padding: "0.2rem 0.55rem" }}>
                  Resolve →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Hops panel */}
        <div className="recon-card" style={{ padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem" }}>
            Hops
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {doc.legs.map((l, i) => (
              <div key={i} data-testid={`chain-leg-row-${i}`} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0.75rem", background: "#f9fafb", borderRadius: "0.4rem", fontSize: "0.82rem" }}>
                <span style={{ display: "inline-block", minWidth: "3.5rem", padding: "0.15rem 0.5rem", borderRadius: "0.35rem", background: "#8B5CF6", color: "white", fontSize: "0.72rem", fontWeight: 700, textAlign: "center" }}>
                  Hop {i + 1}
                </span>
                <span style={{ fontWeight: 600 }}>{l.target_file_name}</span>
                <span style={{ color: "var(--text-muted)" }}>· rule set <b>{l.rule_set_name}</b></span>
                <span style={{ color: "var(--text-muted)" }}>· source <code style={{ fontSize: "0.7rem", background: "#eef2ff", padding: "0.1rem 0.35rem", borderRadius: "0.25rem" }}>{l.anchor_source}</code></span>
                <span style={{ marginLeft: "auto", color: "#16a34a", fontWeight: 600 }}>{l.summary?.matched ?? 0} matched</span>
                <span style={{ color: "#f59e0b" }}>{l.summary?.partial ?? 0} partial</span>
                <span style={{ color: "#a16207" }}>{(l.summary?.ignored_a ?? 0) + (l.summary?.ignored_b ?? 0)} ignored</span>
                <span style={{ color: "var(--text-muted)" }}>of {l.summary?.total_a ?? 0}</span>
                <button onClick={() => navigate(`/results/${l.leg_run_id}`)} className="btn-secondary" style={{ fontSize: "0.72rem", padding: "0.2rem 0.5rem" }}>
                  Open hop
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Waterfall */}
        <div className="recon-card" style={{ padding: "1rem 1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Chain waterfall
            </div>
            <div style={{ display: "flex", gap: "0.25rem", marginLeft: "auto" }}>
              {TABS.map(t => (
                <button key={t.id} data-testid={`chain-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  style={{ padding: "0.3rem 0.75rem", borderRadius: "0.35rem", border: "none", cursor: "pointer",
                           fontSize: "0.78rem", fontWeight: 600,
                           background: tab === t.id ? t.color : "#f3f4f6",
                           color: tab === t.id ? "white" : "var(--text-secondary)" }}>
                  {t.label}
                </button>
              ))}
            </div>
            <input data-testid="chain-waterfall-search"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search any column…"
              className="recon-input"
              style={{ fontSize: "0.82rem", padding: "0.3rem 0.6rem", minWidth: "220px" }} />
          </div>

          {loading && <div style={{ padding: "1rem", color: "var(--text-muted)" }}>Loading…</div>}
          {!loading && waterfall.results.length === 0 && (
            <div data-testid="chain-waterfall-empty" style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              No chains to show for this filter.
            </div>
          )}
          {!loading && waterfall.results.length > 0 && (
            <div style={{ overflow: "auto", maxHeight: "65vh", border: "1px solid #e5e7eb", borderRadius: "0.4rem" }}>
              <table data-testid="chain-waterfall-table" style={{ width: "100%", fontSize: "0.75rem", borderCollapse: "collapse", fontFamily: "JetBrains Mono, monospace" }}>
                <thead style={{ position: "sticky", top: 0, background: "#f3f4f6", zIndex: 1 }}>
                  <tr>
                    {cols.map(c => (
                      <th key={c} style={{ padding: "0.4rem 0.6rem", textAlign: "left", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap", fontSize: "0.68rem", fontWeight: 700, color: "var(--text-secondary)" }}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {waterfall.results.map((r, ri) => (
                    <tr key={r.chain_id || ri} data-testid={`chain-waterfall-row-${ri}`}
                        onClick={async () => {
                          try {
                            const res = await api.get(`/chain-runs/${id}/chain/${r.chain_id}`);
                            setSelectedChain(res.data);
                            setAssignEmail(res.data.assigned_to_email || "");
                          } catch (e) { /* ignore */ }
                        }}
                        style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}>
                      {cols.map(c => (
                        <td key={c} style={{ padding: "0.35rem 0.6rem", whiteSpace: "nowrap", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis" }} title={String(r[c] ?? "")}>
                          {c === "status" ? <StatusBadge status={r[c]} /> :
                           c === "chain_id" ? (
                             <span style={{ color: "var(--text-muted)", fontSize: "0.68rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                               {(doc.chains.find(ch => ch.chain_id === r.chain_id) || {}).forked_from && (
                                 <GitFork size={10} style={{ color: "#8B5CF6" }} title="Forked chain" />
                               )}
                               {String(r[c] || "").slice(0, 8)}…
                             </span>
                           ) :
                           (r[c] === null || r[c] === undefined ? <span style={{ color: "#d1d5db" }}>—</span> : String(r[c]))}
                        </td>
                      ))}
                      {String(r.status || "").startsWith("broken") && (
                        <td style={{ padding: "0.35rem 0.4rem" }}>
                          <button data-testid={`chain-as-exception-${ri}`} title="Open as exception ticket"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await api.post(`/chain-runs/${id}/as-exception`, { chain_id: r.chain_id, notes: `Broken chain: ${r.status}`, flag: "review" });
                                alert("Exception created.");
                              } catch (e2) { alert(e2.response?.data?.detail || e2.message); }
                            }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#f97316", padding: 0 }}>
                            <Flag size={12} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: "0.5rem", fontSize: "0.72rem", color: "var(--text-muted)" }}>
            Showing {waterfall.results.length} of {waterfall.total} chain{waterfall.total !== 1 ? "s" : ""} (max 500 in preview — export CSV for full data).
          </div>
        </div>
      </div>

      {/* Conflict-resolve modal */}
      {resolveOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setResolveOpen(null)} />
          <div data-testid="chain-resolve-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "560px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Resolve chain conflict</h3>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
              Multiple chains claim the same target row at <code>{resolveOpen.hop_key.split(":")[0]}</code>. Pick the winner; the rest will be flagged as <code>conflict_rejected</code>.
            </p>
            <div style={{ maxHeight: "260px", overflow: "auto", marginBottom: "1rem" }}>
              {resolveOpen.chain_ids.map(cid => (
                <label key={cid} style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.3rem 0", fontSize: "0.82rem", cursor: "pointer", fontFamily: "JetBrains Mono, monospace" }}>
                  <input type="radio" name="resolve-pick" data-testid={`pick-${cid}`}
                    onChange={() => setResolveOpen({ ...resolveOpen, _keep: cid })} />
                  {cid.slice(0, 8)}… — status: {(doc.chains.find(c => c.chain_id === cid) || {}).status}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setResolveOpen(null)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="chain-resolve-save" onClick={async () => {
                if (!resolveOpen._keep) return;
                try {
                  await api.post(`/chain-runs/${id}/resolve-conflict`, {
                    hop_key: resolveOpen.hop_key, keep_chain_id: resolveOpen._keep, remarks: "",
                  });
                  const r = await api.get(`/chain-runs/${id}`); setDoc(r.data);
                  setResolveOpen(null);
                } catch (e) { alert(e.response?.data?.detail || e.message); }
              }} className="btn-primary" style={{ fontSize: "0.85rem" }}>
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-resolve modal */}
      {bulkOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => { setBulkOpen(false); setBulkPreview(null); }} />
          <div data-testid="chain-bulk-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "680px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", maxHeight: "80vh", overflow: "auto" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Bulk resolve conflicts</h3>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
              Pick a policy and the conflicts to apply it to. Winners stay, losers are flagged <code>conflict_rejected_at_hop_N</code>.
            </p>

            {/* Policy */}
            <div style={{ marginBottom: "0.85rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, display: "block", marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>Policy</label>
              <select data-testid="bulk-policy" value={bulkPolicy} onChange={e => { setBulkPolicy(e.target.value); setBulkPreview(null); }}
                className="recon-input" style={{ width: "100%", fontSize: "0.88rem" }}>
                <option value="highest_rank">Highest-rank rule wins (lowest sequence in rule set)</option>
                <option value="highest_cost_total">Highest cost_total wins</option>
                <option value="newest_chain">Newest chain wins</option>
                <option value="specific_rule">Specific rule name wins</option>
              </select>
            </div>

            {bulkPolicy === "specific_rule" && (
              <div style={{ marginBottom: "0.85rem" }}>
                <label style={{ fontSize: "0.72rem", fontWeight: 700, display: "block", marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>Rule name</label>
                <input data-testid="bulk-specific-rule" value={bulkSpecificRule} onChange={e => { setBulkSpecificRule(e.target.value); setBulkPreview(null); }}
                  placeholder="e.g. exact_po_number" className="recon-input" style={{ width: "100%", fontSize: "0.88rem" }} />
                <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", margin: "0.25rem 0 0" }}>
                  If no chain at a hop matches this rule, we fall back to <b>highest_rank</b>.
                </p>
              </div>
            )}

            {/* Conflict selection */}
            <div style={{ marginBottom: "0.85rem" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "0.35rem" }}>
                <label style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>
                  Conflicts ({(doc.conflicts || []).length})
                </label>
                <button type="button" data-testid="bulk-select-all"
                  onClick={() => {
                    const all = (doc.conflicts || []).map(c => c.hop_key);
                    setBulkSelected(bulkSelected.length === all.length ? [] : all);
                    setBulkPreview(null);
                  }}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--sage)", fontSize: "0.75rem", fontWeight: 600 }}>
                  {bulkSelected.length === (doc.conflicts || []).length ? "Unselect all" : "Select all"}
                </button>
              </div>
              <div style={{ maxHeight: "240px", overflow: "auto", border: "1px solid #e5e7eb", borderRadius: "0.4rem" }}>
                {(doc.conflicts || []).map((c, i) => {
                  const checked = bulkSelected.includes(c.hop_key);
                  return (
                    <label key={c.hop_key} data-testid={`bulk-row-${i}`}
                      style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.45rem 0.65rem", borderBottom: "1px solid #f3f4f6", fontSize: "0.82rem", cursor: "pointer", background: checked ? "#fff7ed" : "white" }}>
                      <input type="checkbox" checked={checked}
                        onChange={() => { setBulkSelected(checked ? bulkSelected.filter(x => x !== c.hop_key) : [...bulkSelected, c.hop_key]); setBulkPreview(null); }} />
                      <code style={{ background: "#ffedd5", padding: "0.1rem 0.4rem", borderRadius: "0.25rem", fontSize: "0.72rem" }}>{c.hop_key.split(":")[0]}</code>
                      <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                        key {c.hop_key.split(":").slice(1).join(":").slice(0, 40)}
                      </span>
                      <span style={{ marginLeft: "auto", color: "var(--text-muted)", fontSize: "0.72rem" }}>{c.chain_ids.length} chains</span>
                    </label>
                  );
                })}
              </div>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", margin: "0.25rem 0 0" }}>
                Leave empty to apply the policy to <b>all</b> conflicts.
              </p>
            </div>

            <div style={{ marginBottom: "0.85rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, display: "block", marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>
                Remarks (optional, saved with every winner/loser)
              </label>
              <input data-testid="bulk-remarks" value={bulkRemarks} onChange={e => setBulkRemarks(e.target.value)}
                placeholder="e.g. Month-end: apply highest-rank policy" className="recon-input" style={{ width: "100%", fontSize: "0.88rem" }} />
            </div>

            {/* Policy preview ─────────────────────────────────────────────── */}
            <div data-testid="bulk-preview-section" style={{ marginBottom: "0.85rem", border: "1px dashed #c7d2fe", borderRadius: "0.5rem", padding: "0.6rem 0.75rem", background: "#eef2ff" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "0.35rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#4338ca" }}>
                  Preview (dry-run)
                </span>
                <button type="button" data-testid="bulk-preview-refresh"
                  disabled={bulkPreviewBusy}
                  onClick={async () => {
                    setBulkPreviewBusy(true);
                    try {
                      const r = await api.post(`/chain-runs/${id}/bulk-resolve-conflicts`, {
                        hop_keys: bulkSelected, policy: bulkPolicy,
                        specific_rule: bulkPolicy === "specific_rule" ? (bulkSpecificRule || null) : null,
                        dry_run: true,
                      });
                      setBulkPreview(r.data);
                    } catch (e) { alert(e.response?.data?.detail || e.message); }
                    finally { setBulkPreviewBusy(false); }
                  }}
                  style={{ marginLeft: "auto", fontSize: "0.72rem", fontWeight: 600, padding: "0.2rem 0.55rem",
                           background: "#4338ca", color: "white", border: "none", borderRadius: "0.3rem", cursor: "pointer",
                           opacity: bulkPreviewBusy ? 0.6 : 1 }}>
                  {bulkPreviewBusy ? "Previewing…" : (bulkPreview ? "Refresh preview" : "Preview winners")}
                </button>
              </div>
              {!bulkPreview && (
                <p style={{ margin: 0, fontSize: "0.78rem", color: "#4338ca" }}>
                  See who would win each selected conflict <b>before</b> applying. No changes are saved.
                </p>
              )}
              {bulkPreview && (
                <div>
                  <div style={{ fontSize: "0.78rem", color: "#4338ca", marginBottom: "0.45rem" }}>
                    Would resolve <b>{bulkPreview.would_resolve}</b> · would remain <b>{bulkPreview.would_remain}</b> · policy <code>{bulkPreview.policy}</code>
                  </div>
                  <div style={{ maxHeight: "200px", overflow: "auto", background: "white", borderRadius: "0.35rem", border: "1px solid #e0e7ff" }}>
                    {bulkPreview.preview.length === 0 && (
                      <div style={{ padding: "0.55rem 0.7rem", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                        No conflicts match your selection.
                      </div>
                    )}
                    {bulkPreview.preview.map((p, i) => (
                      <div key={p.hop_key} data-testid={`bulk-preview-row-${i}`}
                        style={{ padding: "0.5rem 0.7rem", borderBottom: "1px solid #f3f4f6", fontSize: "0.78rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "0.25rem" }}>
                          <code style={{ background: "#eef2ff", padding: "0.1rem 0.4rem", borderRadius: "0.25rem", fontSize: "0.7rem", color: "#4338ca" }}>
                            hop{p.hop_idx}
                          </code>
                          <span style={{ color: "var(--text-muted)" }}>{p.candidates.length} candidates</span>
                          <span style={{ marginLeft: "auto", color: "var(--text-muted)", fontSize: "0.72rem" }}>
                            will reject <b>{p.will_reject.length}</b>
                          </span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
                          {p.candidates.map(c => {
                            const isWinner = c.chain_id === p.winner_chain_id;
                            return (
                              <span key={c.chain_id}
                                title={`rule: ${c.rule_name || "—"} · cost: ${c.cost_total}`}
                                style={{
                                  padding: "0.15rem 0.5rem", borderRadius: "0.3rem",
                                  fontSize: "0.7rem", fontFamily: "JetBrains Mono, monospace",
                                  background: isWinner ? "#dcfce7" : "#fee2e2",
                                  color: isWinner ? "#166534" : "#991b1b",
                                  border: isWinner ? "1px solid #86efac" : "1px solid #fecaca",
                                  fontWeight: isWinner ? 700 : 500,
                                }}>
                                {isWinner ? "✓ " : ""}{c.chain_id.slice(0, 8)}…
                                <span style={{ opacity: 0.7, marginLeft: "0.35rem" }}>
                                  {c.rule_name || "—"} · {c.cost_total}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => { setBulkOpen(false); setBulkPreview(null); }} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="bulk-resolve-save" disabled={bulkBusy}
                onClick={async () => {
                  setBulkBusy(true);
                  try {
                    const r = await api.post(`/chain-runs/${id}/bulk-resolve-conflicts`, {
                      hop_keys: bulkSelected,
                      policy: bulkPolicy,
                      specific_rule: bulkPolicy === "specific_rule" ? (bulkSpecificRule || null) : null,
                      remarks: bulkRemarks,
                    });
                    const fresh = await api.get(`/chain-runs/${id}`); setDoc(fresh.data);
                    setBulkOpen(false); setBulkSelected([]); setBulkRemarks(""); setBulkPreview(null);
                    alert(`Resolved ${r.data.resolved} conflict${r.data.resolved === 1 ? "" : "s"}. ${r.data.remaining} remaining.`);
                  } catch (e) {
                    alert(e.response?.data?.detail || e.message);
                  } finally { setBulkBusy(false); }
                }} className="btn-primary" style={{ fontSize: "0.85rem", opacity: bulkBusy ? 0.6 : 1 }}>
                {bulkBusy ? "Resolving…" : `Resolve ${bulkSelected.length || "all"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diff modal */}
      {diffOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => { setDiffOpen(false); setDiffResult(null); }} />
          <div data-testid="chain-diff-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "560px" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Diff this run against another</h3>
            <select data-testid="chain-diff-against" value={diffAgainst} onChange={e => setDiffAgainst(e.target.value)}
              className="recon-input" style={{ width: "100%", marginBottom: "0.75rem" }}>
              <option value="">— pick a previous run —</option>
              {otherRuns.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginBottom: "1rem" }}>
              <button onClick={() => { setDiffOpen(false); setDiffResult(null); }} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Close</button>
              <button data-testid="chain-diff-go" disabled={!diffAgainst} onClick={async () => {
                try {
                  const r = await api.get(`/chain-runs/${id}/diff`, { params: { against: diffAgainst } });
                  setDiffResult(r.data);
                } catch (e) { alert(e.response?.data?.detail || e.message); }
              }} className="btn-primary" style={{ fontSize: "0.85rem" }}>Compare</button>
            </div>
            {diffResult && (
              <div data-testid="chain-diff-result" style={{ background: "#f9fafb", padding: "0.75rem", borderRadius: "0.5rem", fontSize: "0.82rem" }}>
                <div>Newly complete: <b style={{ color: "#16a34a" }}>{diffResult.newly_complete}</b></div>
                <div>Newly broken: <b style={{ color: "#dc2626" }}>{diffResult.newly_broken}</b></div>
                <div>Unchanged: <b>{diffResult.unchanged}</b></div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Chain detail side panel (collab: assign + comments + fork indicator) */}
      {selectedChain && (
        <div data-testid="chain-detail-panel" style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: "min(440px, 100vw)", background: "white", borderLeft: "1px solid #e5e7eb", boxShadow: "-4px 0 24px rgba(0,0,0,0.08)", zIndex: 55, overflow: "auto", padding: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1rem", margin: 0, display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <Link2 size={14} style={{ color: "#8B5CF6" }} /> Chain detail
            </h3>
            <button data-testid="chain-detail-close" onClick={() => setSelectedChain(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
              <ShieldX size={14} />
            </button>
          </div>
          <div style={{ fontSize: "0.82rem", marginBottom: "0.75rem", fontFamily: "JetBrains Mono, monospace", color: "var(--text-muted)" }}>
            {selectedChain.chain_id.slice(0, 8)}…
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <StatusBadge status={selectedChain.status} />
            {selectedChain.forked_from && (
              <span data-testid="chain-fork-badge" style={{ marginLeft: "0.5rem", fontSize: "0.72rem", color: "#8B5CF6", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                <GitFork size={11} /> forked from {selectedChain.forked_from.slice(0, 8)}… at hop {Number(selectedChain.forked_at_hop || 0) + 1}
              </span>
            )}
            {selectedChain.is_conflict && (
              <span style={{ marginLeft: "0.5rem", fontSize: "0.72rem", color: "#f97316", fontWeight: 600 }}>⚠ conflict</span>
            )}
          </div>

          <div style={{ marginTop: "0.75rem", padding: "0.5rem 0.75rem", background: "#f9fafb", borderRadius: "0.4rem", fontSize: "0.82rem" }}>
            <div>Hops completed: <b>{selectedChain.hops_completed}</b></div>
            <div>Cost total: <b style={{ color: "#0891b2" }}>${Number(selectedChain.cost_total || 0).toLocaleString()}</b></div>
            <div>Branches dropped: <b>{selectedChain.branches_dropped}</b></div>
          </div>

          {/* Assign */}
          <div style={{ marginTop: "1rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.35rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <UserCircle size={12} /> Owner
            </div>
            <div style={{ display: "flex", gap: "0.35rem" }}>
              <input data-testid="chain-assign-email" value={assignEmail} onChange={e => setAssignEmail(e.target.value)}
                placeholder="assignee email (or blank to unassign)" className="recon-input"
                style={{ flex: 1, fontSize: "0.82rem" }} />
              <button data-testid="chain-assign-btn" onClick={async () => {
                try {
                  await api.post(`/chain-runs/${id}/chain/${selectedChain.chain_id}/assign`, {
                    chain_id: selectedChain.chain_id, assigned_to_email: assignEmail || null,
                  });
                  const r = await api.get(`/chain-runs/${id}/chain/${selectedChain.chain_id}`);
                  setSelectedChain(r.data);
                } catch (e) { alert(e.response?.data?.detail || e.message); }
              }} className="btn-primary" style={{ fontSize: "0.78rem", padding: "0.3rem 0.6rem" }}>
                Save
              </button>
            </div>
            {selectedChain.assigned_to_email && (
              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                Currently: <b>{selectedChain.assigned_to_email}</b>
                {selectedChain.assigned_at && <> · since {new Date(selectedChain.assigned_at).toLocaleDateString()}</>}
              </div>
            )}
          </div>

          {/* Comments */}
          <div style={{ marginTop: "1rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.35rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <MessageSquare size={12} /> Comments ({(selectedChain.comments || []).length})
            </div>
            <div style={{ maxHeight: "200px", overflow: "auto", display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "0.4rem" }}>
              {(selectedChain.comments || []).length === 0 && (
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                  No comments yet.
                </div>
              )}
              {(selectedChain.comments || []).map(c => (
                <div key={c.id} data-testid={`chain-comment-${c.id}`} style={{ padding: "0.5rem 0.65rem", background: "#f9fafb", borderRadius: "0.35rem", fontSize: "0.8rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: "0.2rem" }}>
                    <span><b>{c.author_email}</b></span>
                    <span>{new Date(c.at).toLocaleString()}</span>
                  </div>
                  {c.text}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "0.35rem" }}>
              <input data-testid="chain-comment-input" value={newComment} onChange={e => setNewComment(e.target.value)}
                placeholder="Add a comment…" className="recon-input"
                onKeyDown={async e => {
                  if (e.key === "Enter" && !e.shiftKey && newComment.trim()) {
                    e.preventDefault();
                    try {
                      await api.post(`/chain-runs/${id}/chain/${selectedChain.chain_id}/comment`, {
                        chain_id: selectedChain.chain_id, text: newComment.trim(),
                      });
                      setNewComment("");
                      const r = await api.get(`/chain-runs/${id}/chain/${selectedChain.chain_id}`);
                      setSelectedChain(r.data);
                    } catch (err) { alert(err.response?.data?.detail || err.message); }
                  }
                }}
                style={{ flex: 1, fontSize: "0.82rem" }} />
              <button data-testid="chain-comment-submit" onClick={async () => {
                if (!newComment.trim()) return;
                try {
                  await api.post(`/chain-runs/${id}/chain/${selectedChain.chain_id}/comment`, {
                    chain_id: selectedChain.chain_id, text: newComment.trim(),
                  });
                  setNewComment("");
                  const r = await api.get(`/chain-runs/${id}/chain/${selectedChain.chain_id}`);
                  setSelectedChain(r.data);
                } catch (e) { alert(e.response?.data?.detail || e.message); }
              }} className="btn-primary" style={{ fontSize: "0.78rem", padding: "0.3rem 0.6rem" }}>
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
